// Format currency amount
import { Price } from "@/types/v2";
/**
 * Formats a numeric amount (in cents) into a currency string based on the specified currency code.
 * @param {number | null} amount - The amount in cents to be formatted. If the value is null, it returns "N/A".
 * @param {string} currency - The ISO 4217 currency code (e.g., "USD", "EUR") used to format the numeric value.
 * @returns {string} A formatted currency string based on the provided amount and currency. Returns "N/A" if the amount is null.
 */
export const formatAmount = (amount: number | null, currency: string) => {
  if (amount === null) return "N/A";

  // Convert cents to dollars
  const dollars = amount / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(dollars);
};

// Format recurring interval
/**
 * Formats a recurring interval into a string representation.
 *
 * This function takes a recurring object, typically representing a pricing recurring plan,
 * and formats it into a string indicating the interval and, if applicable, the interval count.
 * @param {object} recurring - The recurring object containing interval details.
 * @param {string} recurring.interval - The interval unit (e.g., "month", "year").
 * @param {number} recurring.interval_count - The number of intervals.
 * @returns {string} A formatted string representing the interval, or an empty string if no interval is provided.
 */
export const formatInterval = (recurring: Price["recurring"]) => {
  if (!recurring) return "";

  const { interval, interval_count } = recurring;

  if (interval_count === 1) {
    return `/${interval}`;
  }

  return `/${interval_count} ${interval}s`;
};
